import type { QueryClient } from '@tanstack/react-query';
import api from '@/axios';
import { PURCHASE_ORDER_WORKFLOW_TRANSITIONS } from '@/configs/purchase-order-workflow-transitions';
import { GET_PURCHASE_ORDER_KEY } from '@/keys';
import type {
  PurchaseOrder,
  PurchaseOrderStatusCode,
} from '@/types/core/purchase-order/purchase-order';
import type { UploadPurchaseOrderDocumentParams } from '@/types/core/purchase-order/upload-purchase-order-document-params';

export const PROFORMA_UNSIGNED_DOCUMENT_TYPE_CODE = 'proforma_unsigned';

const getCachedPurchaseOrder = (
  queryClient: QueryClient,
  purchaseOrderId: string,
): PurchaseOrder | undefined =>
  queryClient
    .getQueriesData<PurchaseOrder>({
      queryKey: [...GET_PURCHASE_ORDER_KEY, purchaseOrderId],
    })
    .find(([, purchaseOrder]) => purchaseOrder)?.[1];

const resolveUploadTransitionFromStatus = (
  statusCode: PurchaseOrderStatusCode | null,
): string => {
  switch (statusCode) {
    case 'WAITING_FOR_SUPPLIER_AGREEMENT':
      return PURCHASE_ORDER_WORKFLOW_TRANSITIONS.UPLOAD_UNSIGNED_PROFORMA_WAITING_FOR_SUPPLIER_AGREEMENT;
    case 'SUPPLIER_AGREEMENT_RECEIVED':
      return PURCHASE_ORDER_WORKFLOW_TRANSITIONS.UPLOAD_UNSIGNED_PROFORMA_SUPPLIER_AGREEMENT_RECEIVED;
    case 'IN_ADJUSTMENTS':
      return PURCHASE_ORDER_WORKFLOW_TRANSITIONS.UPLOAD_UNSIGNED_PROFORMA_IN_ADJUSTMENTS;
    case 'IN_PRODUCTION':
      return PURCHASE_ORDER_WORKFLOW_TRANSITIONS.UPLOAD_UNSIGNED_PROFORMA_IN_PRODUCTION;
    default:
      throw new Error(
        `No upload transition is configured for purchase order status "${statusCode ?? 'null'}".`,
      );
  }
};

const uploadUnsignedProformaDocumentWithTransition = async ({
  purchaseOrderId,
  typeId,
  file,
  transitionName,
  recipients,
  message,
}: UploadPurchaseOrderDocumentParams & {
  transitionName: string;
}): Promise<void> => {
  const formData = new FormData();
  formData.append('typeId', String(typeId));
  formData.append('file', file);
  formData.append('transitionName', transitionName);
  if (recipients) {
    for (const recipient of recipients) {
      formData.append('recipients[]', recipient);
    }
  }
  if (message) {
    formData.append('message', message);
  }

  await api.post(
    `/purchase-orders/${purchaseOrderId}/update-status`,
    formData,
    {
      transformRequest: [
        (data, headers) => {
          if (data instanceof FormData) {
            headers['Content-Type'] = undefined;
          }
          return data;
        },
      ],
    },
  );
};

export const uploadUnsignedProformaDocument = (
  queryClient: QueryClient,
  params: UploadPurchaseOrderDocumentParams,
): Promise<void> => {
  const purchaseOrder = getCachedPurchaseOrder(
    queryClient,
    params.purchaseOrderId,
  );
  const transitionName = resolveUploadTransitionFromStatus(
    purchaseOrder?.statusCode ?? null,
  );

  return uploadUnsignedProformaDocumentWithTransition({
    ...params,
    transitionName,
  });
};
